import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type {
  WalletPageResponse,
  WalletInfo,
  NFTPortfolio,
  WalletOffers,
  WalletTransaction,
} from '../types/wallet';

/**
 * Query key factory for wallet-related queries
 * Centralizes query keys to make invalidation easier
 */
export const walletQueryKeys = {
  wallet: (address: string) => ['wallet', address] as const,
  nfts: (address: string, page?: number) => ['wallet', address, 'nfts', page] as const,
  offers: (address: string) => ['wallet', address, 'offers'] as const,
  transactions: (address: string) => ['wallet', address, 'transactions'] as const,
};

/**
 * Complete wallet data returned by the API
 */
export interface WalletData {
  wallet: WalletInfo;
  nfts: NFTPortfolio;
  offers: WalletOffers;
  transactions: {
    transactions: WalletTransaction[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Hook to fetch complete wallet data
 *
 * Fetches NFT portfolio, offers, and transaction history for a wallet.
 * Uses TanStack Query for caching and state management.
 *
 * @param address - Wallet address (XRPL r-address)
 * @param page - Page number for NFT pagination (default: 1)
 * @param limit - NFTs per page (default: 100, max: 100)
 * @param txPage - Transaction page number (default: 1)
 * @param txLimit - Transactions per page (default: 300, max: 300)
 * @returns Query result with wallet data
 */
export function useWallet(
  address: string | undefined,
  page: number = 1,
  limit: number = 100,
  txPage: number = 1,
  txLimit: number = 300
) {
  return useQuery({
    queryKey: walletQueryKeys.wallet(address || ''),
    queryFn: async () => {
      if (!address) throw new Error('Wallet address is required');

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        txPage: String(txPage),
        txLimit: String(txLimit),
      });

      const response = await apiClient.get<WalletPageResponse>(
        `/wallets/${address}?${params.toString()}`
      );

      if (!response.success || !response.data) {
        throw new Error('Wallet not found');
      }

      return response.data as WalletData;
    },
    enabled: !!address,
    staleTime: 30 * 1000, // 30 seconds (static snapshot model)
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to fetch only NFT portfolio data
 *
 * Useful when only the NFTs tab is needed.
 *
 * @param address - Wallet address
 * @param page - Page number for NFT pagination
 * @param limit - NFTs per page
 * @returns Query result with NFT portfolio
 */
export function useWalletNFTs(
  address: string | undefined,
  page: number = 1,
  limit: number = 100
) {
  const { data, ...rest } = useWallet(address, page, limit);

  return {
    data: data?.nfts,
    ...rest,
  };
}

/**
 * Hook to fetch only wallet offers data
 *
 * Useful when only the Offers tab is needed.
 *
 * @param address - Wallet address
 * @returns Query result with wallet offers
 */
export function useWalletOffers(address: string | undefined) {
  const { data, ...rest } = useWallet(address);

  return {
    data: data?.offers,
    ...rest,
  };
}

/**
 * Hook to fetch only wallet transaction history
 *
 * Useful when only the History tab is needed.
 *
 * @param address - Wallet address
 * @returns Query result with transaction history
 */
export function useWalletTransactions(address: string | undefined) {
  const { data, ...rest } = useWallet(address);

  return {
    data: data?.transactions,
    ...rest,
  };
}
